// ============================================================
// WordSystem — processes word input and spawns entities
// ============================================================
import { parseWordInput, buildEntityConfig, WORD_DICTIONARY, MODIFIERS } from '../data/wordDictionary.js';
import { SpawnedEntity } from '../entities/SpawnedEntity.js';
import { PhysicsEngine } from '../physics/PhysicsEngine.js';
import { MAX_SPAWNED_OBJECTS, WORD_COOLDOWN } from '../constants.js';

export class WordSystem {
  /**
   * @param {PhysicsEngine} physics
   * @param {PIXI.Container} worldContainer
   */
  constructor(physics, worldContainer) {
    this.physics   = physics;
    this.container = worldContainer;
    this.player    = null;   // set by Game after player is created
    /** @type {SpawnedEntity[]} */
    this.entities  = [];
    this._lastSpawn = 0;
    this.spawnHistory = [];   // [{word, config, time}]
    this.onSpawn = null;      // callback(entity, config, word)
    this.onError = null;      // callback(msg)
  }

  /**
   * Parse input and return a ready-to-place config WITHOUT spawning.
   * The caller (Game/DragSystem) decides where to actually place it.
   *
   * @returns {{ success:boolean, msg:string, config?:object, label?:string }}
   */
  prepare(input) {
    const now = Date.now();
    if (now - this._lastSpawn < WORD_COOLDOWN) {
      return { success: false, msg: `Wait a moment… ⏳` };
    }

    const { modifiers, word } = parseWordInput(input);
    if (!word) {
      return {
        success: false,
        msg: `"${input}" not found. Try: ladder, bridge, trampoline, balloon…`,
      };
    }

    const config = buildEntityConfig(word, modifiers);
    if (!config) return { success: false, msg: `Can't create "${word}".` };

    const modStr = modifiers.length > 0 ? modifiers.join(' ') + ' ' : '';
    const label  = `${modStr}${word}`;
    return { success: true, config, label, word };
  }

  /**
   * Actually spawn a prepared config at exact world coordinates.
   * @param {object} config  - from prepare()
   * @param {number} x       - world X (centre of object)
   * @param {number} y       - world Y (centre of object)
   * @param {string} label
   */
  spawnAt(config, x, y, label = '') {
    // Enforce max objects
    if (this.entities.length >= MAX_SPAWNED_OBJECTS) {
      const oldest = this.entities.shift();
      this.physics.removeBody(oldest.body);
      oldest.destroy();
    }

    const entity = new SpawnedEntity(config, x, y, this.container);
    entity._player = this.player;
    this.container.__physBodies = this.physics.bodies;
    this.physics.addBody(entity.body);
    this.entities.push(entity);
    this._lastSpawn = Date.now();

    this.spawnHistory.push({ word: label, config, time: Date.now() });
    this.onSpawn?.(entity, config, label);
    return entity;
  }

  /**
   * Legacy convenience — parse + immediately spawn near (spawnX, spawnY).
   * Still used as fallback if DragSystem is unavailable.
   */
  process(input, spawnX, spawnY) {
    const result = this.prepare(input);
    if (!result.success) return result;

    const spawnOffset = 80 + result.config.w / 2;
    const ex = spawnX + (Math.random() < 0.5 ? spawnOffset : -spawnOffset);
    const ey = spawnY - result.config.h / 2 - 20;

    const entity = this.spawnAt(result.config, ex, ey, result.label);
    return {
      success: true,
      msg: `✨ ${result.config.emoji ?? ''} ${result.label} — drag it where you want!`,
      entity,
    };
  }

  /** Remove a specific spawned entity */
  removeEntity(entity) {
    const i = this.entities.indexOf(entity);
    if (i !== -1) {
      this.entities.splice(i, 1);
      this.physics.removeBody(entity.body);
      entity.destroy();
    }
  }

  /** Update all spawned entities */
  update(dt) {
    for (let i = this.entities.length - 1; i >= 0; i--) {
      const e = this.entities[i];
      if (!e.alive) {
        this.entities.splice(i, 1);
        this.physics.removeBody(e.body);
        continue;
      }
      e.update(dt);

      // Kill entities that fall off the world
      if (e.body.y > 3500 || e.body.y < -500 || e.body.x < -500 || e.body.x > 5000) {
        e.alive = false;
      }
    }
  }

  /** Get autocomplete suggestions for partial input */
  getSuggestions(partial) {
    const lower = partial.toLowerCase().trim();
    if (!lower) return [];

    const words = Object.keys(WORD_DICTIONARY);
    const mods  = Object.keys(MODIFIERS);
    const all   = [...mods, ...words];

    return all
      .filter(w => w.startsWith(lower))
      .slice(0, 8);
  }

  /** Clear all spawned entities */
  clear() {
    for (const e of this.entities) {
      this.physics.removeBody(e.body);
      e.destroy();
    }
    this.entities.length = 0;
  }
}
